import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	Typography,
	Button,
	Box,
	Avatar,
	IconButton,
	Tooltip,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Chip,
	CircularProgress,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CreateIcon from "@mui/icons-material/Create";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import CampaignIcon from "@mui/icons-material/Campaign";
import TourSettingsDialog from "../JoyrideTours/TourSettingsDialog";
import NewReleasesModal from "../Modals/NewReleasesModal";
import JiraTicketModal from "../Modals/JiraTicketModal";
import TicketStatusModal from "../Modals/TicketStatusModal";
import ProjectInfoModal from "../Modals/ProjectInfoModal";
import CognitoUserManagementModal from "../Modals/CognitoUserManagementModal";
import NotificationBanner from "../misc/NotificationBanner";
import NotificationBannerAdmin from "../misc/NotificationBannerAdmin";
import useNotificationBanner from "../../hooks/useNotificationBanner";
import { useUserAccess } from "../../hooks/useUserAccess";
import { useLanguage } from "../../hooks/useLanguage";
import {
	LANGUAGE_STORAGE_KEY,
	getStoredLanguage,
} from "../../utils/i18n";
import { isDevBranchEnvironment } from "../../utils/apiConfig";
import "../../assets/style.css";

// On the development host the active-nav accent is green; elsewhere it's red.
const ACTIVE_NAV_COLOR = isDevBranchEnvironment() ? "#2e7d32" : "#cc0000";
const ACTIVE_NAV_COLOR_FADED = isDevBranchEnvironment()
	? "#2e7d3240"
	: "#cc000040";

// ── Data warning banner is now managed dynamically via S3 (admin-controlled) ──

const NAV_ITEMS = [
	{ labelKey: "header.nav.supplyType", path: "/supply-type-allocation" },
	{ labelKey: "header.nav.vehicleTracking", path: "/vff" },
	{ labelKey: "header.nav.seriesTracking", path: "/series-tracking" },
];

// Available UI languages. Flags live in /public and are served from the root.
const LANGUAGES = [
	{ code: "EN", label: "English", flag: "/gb.png" },
	{ code: "DE", label: "Deutsch", flag: "/de.png" },
	{ code: "SE", label: "Svenska", flag: "/se.png" },
];

function Header({ signOut, user, onStartTour }) {
	const [tourSettingsOpen, setTourSettingsOpen] = useState(false);
	const [newReleasesOpen, setNewReleasesOpen] = useState(false);
	const [jiraTicketOpen, setJiraTicketOpen] = useState(false);
	const [ticketStatusOpen, setTicketStatusOpen] = useState(false);
	const [projectInfoOpen, setProjectInfoOpen] = useState(false);
	const [cognitoModalOpen, setCognitoModalOpen] = useState(false);
	const [bannerAdminOpen, setBannerAdminOpen] = useState(false);
	const [jiraMenuAnchor, setJiraMenuAnchor] = useState(null);
	const [userMenuAnchor, setUserMenuAnchor] = useState(null);
	const [activeNav, setActiveNav] = useState(null);
	const [langMenuAnchor, setLangMenuAnchor] = useState(null);
	const [language, setLanguage] = useState(() => getStoredLanguage());
	const { t } = useLanguage();

	const currentLanguage =
		LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

	const handleLanguageSelect = (code) => {
		setLanguage(code);
		localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
		setLangMenuAnchor(null);
		// Notify the rest of the app so any listeners can react to the change.
		window.dispatchEvent(
			new CustomEvent("languagechange", { detail: { language: code } }),
		);
	};

	const location = useLocation();
	const navigate = useNavigate();
	const INNER_PAGES = [
		"/supply-type-allocation",
		"/vff",
		"/series-tracking",
		"/dashboard",
		"/vehicle-planning",
		"/shopfloor-production",
	];
	const isShopfloorPage = location.pathname.startsWith("/shopfloor-production");

	const isVehiclePlanningPage = location.pathname === "/vehicle-planning";

	const isInnerPage = INNER_PAGES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));

	const getUserEmail = () => {
		if (user?.profile?.email) return user.profile.email;
		if (user?.profile?.preferred_username?.includes("@"))
			return user.profile.preferred_username;
		return localStorage.getItem("username") || "user@example.com";
	};

	const { userAccess, isLoading: isLoadingAccess } = useUserAccess(
		getUserEmail(),
	);

	// Map pathname → banner page slug so the banner can be filtered per-page
	const PATHNAME_TO_PAGE_SLUG = {
		"/": "landing",
		"/supply-type-allocation": "supply-type-allocation",
		"/vff": "vff",
		"/series-tracking": "series-tracking",
		"/dashboard": "dashboard",
		"/vehicle-planning": "vehicle-planning",
		"/shopfloor-production": "shopfloor-production",
	};
	const currentPageSlug = PATHNAME_TO_PAGE_SLUG[location.pathname] ?? null;

	// Keep --banner-height CSS variable in sync so page containers adjust paddingTop
	const { banner } = useNotificationBanner();
	const BANNER_HEIGHT = 34; // px — matches the rendered banner bar height
	useEffect(() => {
		// Banner is visible only when enabled, has a message, AND the current page
		// is in the targeted pages list (or the list is empty = all pages)
		const pageMatch =
			!currentPageSlug ||
			banner.pages.length === 0 ||
			banner.pages.includes(currentPageSlug);
		const height = banner.enabled && banner.message && pageMatch ? BANNER_HEIGHT : 0;
		document.documentElement.style.setProperty(
			"--banner-height",
			`${height}px`,
		);
	}, [banner.enabled, banner.message, banner.pages, currentPageSlug]);

	const isAdmin = () => {
		const adminEmails = [
			"yash.sakhare@man.eu",
			"anuradha.shukla@man.eu",
			"dhiraj.chaudhari@man.eu",
			"vishal.talwar@man.eu",
			"abhilash.walunj@man.eu",
			"anagha.shinde@man.eu",
			"tanmay.bhosale@man.eu",
			"pankaj.thete@man.eu",
		];
		return adminEmails.some(
			(e) => e.toLowerCase() === getUserEmail().toLowerCase(),
		);
	};
	const getUserDisplayName = () => {
		if (user?.profile?.preferred_username)
			return user.profile.preferred_username;
		if (user?.profile?.email) return user.profile.email.split("@")[0];
		const stored = localStorage.getItem("username");
		if (stored) return stored.includes("@") ? stored.split("@")[0] : stored;
		return "User";
	};

	const getFullName = () => {
		const email = getUserEmail();
		const localPart = email.split("@")[0];
		return localPart
			.split(".")
			.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
			.join(" ");
	};
	const handleNavClick = (path) => {
		setActiveNav(path);
		navigate(path);
	};

	return (
		<div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1100 }}>
			{" "}
			<header
				style={{
					display: "flex",
					alignItems: "center",
					backgroundColor: "#f8f8f8",
					height: "74px",
					padding: "0 24px",
					boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
					gap: "0",
					position: "relative",
				}}
			>
				{/* Logo — clicking returns to landing page */}
				<Tooltip
					title={t("header.logoTooltip")}
					placement="right"
					arrow
				>
					<div
						onClick={() => navigate("/")}
						style={{
							display: "flex",
							flexDirection: (isVehiclePlanningPage || isShopfloorPage) ? "row" : "column",
							alignItems: (isVehiclePlanningPage || isShopfloorPage) ? "center" : "flex-start",
							flexShrink: 0,
							marginRight: "22px",
							cursor: "pointer",
							gap: (isVehiclePlanningPage || isShopfloorPage) ? "12px" : "0",
						}}
					>
						<img
							src="/man1.png"
							alt="MAN"
							style={{ height: "55px", width: "auto", marginLeft: (isVehiclePlanningPage || isShopfloorPage) ? "0" : "18px" }}
							onError={(e) => {
								e.currentTarget.style.display = "none";
							}}
						/>
						{isInnerPage && (
							<span
								style={{
									fontSize: (isVehiclePlanningPage || isShopfloorPage) ? "1.5rem" : "1rem",
									color: "black",
									fontFamily: "MAN EUROPE_Bold",
									marginTop: (isVehiclePlanningPage || isShopfloorPage) ? "8px" : "-8px",
									marginLeft: (isVehiclePlanningPage || isShopfloorPage) ? "5px" : "-12px",
									whiteSpace: "nowrap",
									letterSpacing: "0.03em",
								}}
							>
								{isVehiclePlanningPage
									? t("header.titleVehiclePlanning")
									: isShopfloorPage
									? t("header.titleShopfloor")
									: t("header.titleParts")}
							</span>
						)}{" "}
					</div>
				</Tooltip>

				{/* App title on landing page — sits right beside the logo in normal flex flow*/}
				{!isInnerPage && (
					<span
						style={{
							fontSize: "2rem",
							fontWeight: "700",
							color: "#1e2832",
							fontFamily: "MAN EUROPE_Bold",
							letterSpacing: "0.01em",
							whiteSpace: "nowrap",
						}}
					>
						{t("header.appTitle")}
					</span>
				)}
				{/* DEV environment chip — dev host + localhost only */}
				{isDevBranchEnvironment() && (
					<Chip
						label="Development Environment"
						size="medium"
						sx={{
							ml: 11,
							height: "35px",
							backgroundColor: "#2e7d32",
							color: "white",
							fontWeight: 900,
							fontSize: "1rem",
							letterSpacing: "0.06em",
							fontFamily: "MAN EUROPE_Bold, sans-serif",
							"& .MuiChip-label": { px: 1.5 },
						}}
					/>
				)}				{/* Nav tabs — centered absolutely on inner pages only */}
				<nav
					style={{
						position: "absolute",
						left: "50%",
						transform: "translateX(-50%)",
						display: "flex",
						alignItems: "center",
						height: "74px",
						pointerEvents: "auto",
					}}
				>
					{" "}
					{isInnerPage && location.pathname !== "/vehicle-planning" && !isShopfloorPage
						? NAV_ITEMS.map((item) => {
								const isActive = location.pathname === item.path;
								return (
									<a
										key={item.path}
										href={item.path}
										onClick={(e) => {
											if (
												!e.ctrlKey &&
												!e.metaKey &&
												!e.shiftKey &&
												e.button !== 1
											) {
												e.preventDefault();
												handleNavClick(item.path);
											}
										}}
										style={{
											display: "inline-flex",
											alignItems: "flex-end",
											textDecoration: "none",
											backgroundColor: "transparent",
											borderBottom: isActive
												? `2px solid ${ACTIVE_NAV_COLOR}`
												: "2px solid transparent",
											color: isActive ? ACTIVE_NAV_COLOR : "#555",
											fontSize: "1.2rem",
											fontWeight: isActive ? "1000" : "500",
											cursor: "pointer",
											padding: "0 25px 0px 16px",
											paddingTop: "30px",
											height: "74px",
											whiteSpace: "nowrap",
											fontFamily: "MAN EUROPE_Bold, sans-serif",
											transition: "all 0.15s ease",
											boxSizing: "border-box",
										}}
										onMouseEnter={(e) => {
											if (!isActive) {
												e.currentTarget.style.color = ACTIVE_NAV_COLOR;
												e.currentTarget.style.borderBottomColor =
													ACTIVE_NAV_COLOR_FADED;
											}
										}}
										onMouseLeave={(e) => {
											if (!isActive) {
												e.currentTarget.style.color = "#555";
												e.currentTarget.style.borderBottomColor = "transparent";
											}
										}}
									>
										{t(item.labelKey)}
									</a>
								);
							})
						: null}
				</nav>
				{/* Right section */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
						flexShrink: 0,
						marginLeft: "auto",
					}}
				>
					{/* Search icon */}
					{/* <IconButton size="small" sx={{ color: '#555' }}>
            <SearchIcon fontSize="small" />
          </IconButton> */}
					{/* Notifications icon */}
					{/* <IconButton size="small" sx={{ color: '#555' }}>
            <NotificationsOutlinedIcon fontSize="small" />
          </IconButton> */}
					{/* Language selector */}
					<Tooltip title={t("header.changeLanguage")} arrow>
						<div
							onClick={(e) => setLangMenuAnchor(e.currentTarget)}
							style={{
								display: "flex",
								alignItems: "center",
								border: "1px solid #e0e0e0",
								borderRadius: "20px",
								padding: "4px 10px",
								fontSize: "0.8rem",
								fontWeight: "600",
								color: "#333",
								cursor: "pointer",
								gap: "6px",
								transition: "background 0.15s",
							}}
							onMouseEnter={(e) =>
								(e.currentTarget.style.backgroundColor = "#f5f5f5")
							}
							onMouseLeave={(e) =>
								(e.currentTarget.style.backgroundColor = "transparent")
							}
						>
							<img
								src={currentLanguage.flag}
								alt={currentLanguage.label}
								style={{
									width: "20px",
									height: "14px",
									objectFit: "cover",
									borderRadius: "2px",
									display: "block",
								}}
							/>
							{currentLanguage.code}
							<KeyboardArrowDownIcon sx={{ color: "#aaa", fontSize: "16px" }} />
						</div>
					</Tooltip>
					{/* Language submenu */}
					<Menu
						anchorEl={langMenuAnchor}
						open={Boolean(langMenuAnchor)}
						onClose={() => setLangMenuAnchor(null)}
						sx={{
							"& .MuiPaper-root": {
								backgroundColor: "white",
								border: "1px solid #DFE1E6",
								borderRadius: "8px",
								boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
								minWidth: "160px",
								mt: 1,
							},
						}}
					>
						{LANGUAGES.map((lang) => (
							<MenuItem
								key={lang.code}
								selected={lang.code === language}
								onClick={() => handleLanguageSelect(lang.code)}
								sx={{
									padding: "10px 16px",
									"&:hover": { backgroundColor: "#F4F5F7" },
									"&.Mui-selected": { backgroundColor: "#F4F5F7" },
									"&.Mui-selected:hover": { backgroundColor: "#ECEEF0" },
								}}
							>
								<ListItemIcon sx={{ minWidth: "32px" }}>
									<img
										src={lang.flag}
										alt={lang.label}
										style={{
											width: "22px",
											height: "15px",
											objectFit: "cover",
											borderRadius: "2px",
											display: "block",
										}}
									/>
								</ListItemIcon>
								<ListItemText
									primary={lang.label}
									sx={{
										"& .MuiTypography-root": {
											fontFamily: "MAN EUROPE_Bold, sans-serif",
											fontSize: "13px",
											fontWeight: lang.code === language ? 700 : 500,
										},
									}}
								/>
							</MenuItem>
						))}
					</Menu>
					{/* Jira Tickets button */}
					<Button
						onClick={(e) => setJiraMenuAnchor(e.currentTarget)}
						variant="contained"
						endIcon={<span style={{ fontSize: "0.7rem" }}></span>}
						sx={{
							backgroundColor: "#0052CC",
							color: "white",
							fontFamily: "MAN EUROPE_Bold, sans-serif",
							fontSize: "0.8rem",
							textTransform: "none",
							borderRadius: "20px",
							padding: "5px 14px",
							boxShadow: "none",
							"&:hover": { backgroundColor: "#0041A3", boxShadow: "none" },
						}}
					>
						{t("header.jiraTickets")}
					</Button>
					{/* JIRA submenu */}
					<Menu
						anchorEl={jiraMenuAnchor}
						open={Boolean(jiraMenuAnchor)}
						onClose={() => setJiraMenuAnchor(null)}
						sx={{
							"& .MuiPaper-root": {
								backgroundColor: "white",
								border: "1px solid #DFE1E6",
								borderRadius: "8px",
								boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
								minWidth: "180px",
								mt: 1,
							},
						}}
					>
						<MenuItem
							onClick={() => {
								setJiraTicketOpen(true);
								setJiraMenuAnchor(null);
							}}
							sx={{
								padding: "10px 16px",
								"&:hover": { backgroundColor: "#F4F5F7" },
							}}
						>
							<ListItemIcon sx={{ minWidth: "32px" }}>
								<CreateIcon sx={{ fontSize: "16px", color: "#0052CC" }} />
							</ListItemIcon>
							<ListItemText
								primary={t("header.createTicket")}
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										fontSize: "13px",
									},
								}}
							/>
						</MenuItem>
						<MenuItem
							onClick={() => {
								setTicketStatusOpen(true);
								setJiraMenuAnchor(null);
							}}
							sx={{
								padding: "10px 16px",
								"&:hover": { backgroundColor: "#F4F5F7" },
							}}
						>
							<ListItemIcon sx={{ minWidth: "32px" }}>
								<AssignmentIcon sx={{ fontSize: "16px", color: "#0052CC" }} />
							</ListItemIcon>
							<ListItemText
								primary={t("header.myTickets")}
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										fontSize: "13px",
									},
								}}
							/>
						</MenuItem>
					</Menu>
					{/* User avatar + name + dropdown */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "10px",
							cursor: "pointer",
							padding: "4px 8px",
							borderRadius: "8px",
							transition: "background 0.15s",
						}}
						onClick={(e) => setUserMenuAnchor(e.currentTarget)}
						onMouseEnter={(e) =>
							(e.currentTarget.style.backgroundColor = "#f5f5f5")
						}
						onMouseLeave={(e) =>
							(e.currentTarget.style.backgroundColor = "transparent")
						}
					>
						<Avatar
							sx={{
								width: 36,
								height: 36,
								backgroundColor: "#1e2832",
								fontSize: "0.9rem",
								fontWeight: 700,
							}}
						>
							{getUserDisplayName().charAt(0).toUpperCase()}
						</Avatar>
						<div style={{ display: "flex", flexDirection: "column" }}>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "6px",
								}}
							>
								<span
									style={{
										fontSize: "0.85rem",
										fontWeight: "700",
										color: "#1e2832",
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										whiteSpace: "nowrap",
									}}
								>
									{getFullName()}
								</span>
								{isLoadingAccess ? (
									<CircularProgress size={10} sx={{ color: "#aaa" }} />
								) : (
									<Chip
										label={
											isAdmin() || userAccess?.user_role === "admin"
												? t("header.roleAdmin")
												: t("header.roleUser")
										}
										size="small"
										sx={{
											height: "16px",
											fontSize: "0.6rem",
											fontWeight: 700,
											backgroundColor:
												isAdmin() || userAccess?.user_role === "admin"
													? "#1976d2"
													: "#888",
											color: "white",
											"& .MuiChip-label": { px: 0.8, py: 0 },
										}}
									/>
								)}
							</div>
							<span
								style={{
									fontSize: "0.72rem",
									color: "#888",
									whiteSpace: "nowrap",
								}}
							>
								{getUserEmail()}
							</span>
						</div>
						<KeyboardArrowDownIcon sx={{ color: "#aaa", fontSize: "18px" }} />
					</div>
					{/* User dropdown menu */}{" "}
					<Menu
						anchorEl={userMenuAnchor}
						open={Boolean(userMenuAnchor)}
						onClose={() => setUserMenuAnchor(null)}
						disableAutoFocusItem
						sx={{
							"& .MuiPaper-root": {
								backgroundColor: "white",
								border: "1px solid #DFE1E6",
								borderRadius: "8px",
								boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
								minWidth: "220px",
								mt: 1,
							},
							"& .MuiMenuItem-root.Mui-focusVisible": {
								backgroundColor: "transparent",
								outline: "none",
							},
							"& .MuiMenuItem-root:focus": {
								outline: "none",
							},
						}}
					>
						{" "}
						{/* New Releases */}
						<MenuItem
							onClick={() => {
								setNewReleasesOpen(true);
								setUserMenuAnchor(null);
							}}
							sx={{
								padding: "10px 16px",
								"&:hover": { backgroundColor: "#F4F5F7" },
							}}
						>
							<ListItemIcon sx={{ minWidth: "32px" }}>
								<NewReleasesIcon sx={{ fontSize: "16px", color: "#cc0000" }} />
							</ListItemIcon>
							<ListItemText
								primary={t("header.newReleases")}
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										fontSize: "13px",
									},
								}}
							/>
						</MenuItem>
						{/* User Guide */}
						<MenuItem
							onClick={() => {
								setUserMenuAnchor(null);
								onStartTour();
							}}
							sx={{
								padding: "10px 16px",
								"&:hover": { backgroundColor: "#F4F5F7" },
							}}
						>
							<ListItemIcon sx={{ minWidth: "32px" }}>
								<HelpOutlineIcon sx={{ fontSize: "16px", color: "#cc0000" }} />
							</ListItemIcon>
							<ListItemText
								primary={t("header.userGuide")}
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										fontSize: "13px",
									},
								}}
							/>
						</MenuItem>
						{/* Project Info */}
						<MenuItem
							onClick={() => {
								setProjectInfoOpen(true);
								setUserMenuAnchor(null);
							}}
							sx={{
								padding: "10px 16px",
								"&:hover": { backgroundColor: "#F4F5F7" },
							}}
						>
							<ListItemIcon sx={{ minWidth: "32px" }}>
								<SettingsIcon sx={{ fontSize: "16px", color: "#cc0000" }} />
							</ListItemIcon>
							<ListItemText
								primary={t("header.projectInfo")}
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										fontSize: "13px",
									},
								}}
							/>
						</MenuItem>{" "}
						{/* Manage Users (Admin only) */}
						{isAdmin() && (
							<MenuItem
								onClick={() => {
									setCognitoModalOpen(true);
									setUserMenuAnchor(null);
								}}
								sx={{
									padding: "10px 16px",
									"&:hover": { backgroundColor: "#F4F5F7" },
								}}
							>
								<ListItemIcon sx={{ minWidth: "32px" }}>
									<PersonAddIcon sx={{ fontSize: "16px", color: "#6a1b9a" }} />
								</ListItemIcon>
								<ListItemText
									primary={t("header.manageUsers")}
									sx={{
										"& .MuiTypography-root": {
											fontFamily: "MAN EUROPE_Bold, sans-serif",
											fontSize: "13px",
										},
									}}
								/>
							</MenuItem>
						)}
						{/* Manage Notifications Banner (Admin only) */}
						{isAdmin() && (
							<MenuItem
								onClick={() => {
									setBannerAdminOpen(true);
									setUserMenuAnchor(null);
								}}
								sx={{
									padding: "10px 16px",
									"&:hover": { backgroundColor: "#F4F5F7" },
								}}
							>
								<ListItemIcon sx={{ minWidth: "32px" }}>
									<CampaignIcon sx={{ fontSize: "16px", color: "#e65100" }} />
								</ListItemIcon>
								<ListItemText
									primary={t("header.manageBanner")}
									sx={{
										"& .MuiTypography-root": {
											fontFamily: "MAN EUROPE_Bold, sans-serif",
											fontSize: "13px",
										},
									}}
								/>
							</MenuItem>
						)}						{/* FZG Planning */}
						<MenuItem
							onClick={() => {
								setUserMenuAnchor(null);
								navigate("/vehicle-planning");
							}}
							sx={{
								padding: "10px 16px",
								"&:hover": { backgroundColor: "#F4F5F7" },
							}}
						>
							<ListItemIcon sx={{ minWidth: "32px" }}>
								<AssignmentIcon sx={{ fontSize: "16px", color: "#2e7d32" }} />
							</ListItemIcon>
							<ListItemText
								primary={t("header.fzgPlanning")}
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										fontSize: "13px",
									},
								}}
							/>
						</MenuItem>
						{/* Shopfloor Production */}
						<MenuItem
							onClick={() => {
								setUserMenuAnchor(null);
								navigate("/shopfloor-production");
							}}
							sx={{
								padding: "10px 16px",
								"&:hover": { backgroundColor: "#F4F5F7" },
							}}
						>
							<ListItemIcon sx={{ minWidth: "32px" }}>
								<AssignmentIcon sx={{ fontSize: "16px", color: "#e65100" }} />
							</ListItemIcon>
							<ListItemText
								primary={t("header.shopfloorProduction")}
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										fontSize: "13px",
									},
								}}
							/>
						</MenuItem>
						{/* Sign out */}
						<MenuItem
							onClick={() => {
								setUserMenuAnchor(null);
								if (signOut) signOut();
							}}
							sx={{
								padding: "10px 16px",
								"&:hover": { backgroundColor: "#fff5f5" },
								borderTop: "1px solid #f0f0f0",
								mt: 0.5,
							}}
						>
							<ListItemText
								primary={t("header.signOut")}
								sx={{
									"& .MuiTypography-root": {
										fontFamily: "MAN EUROPE_Bold, sans-serif",
										fontSize: "13px",
										color: "#cc0000",
									},
								}}
							/>
						</MenuItem>
					</Menu>{" "}
				</div>
			</header>
			{/* Notification banner — sits below the header bar, still inside the fixed wrapper */}
			<NotificationBanner variant="bar" currentPage={currentPageSlug} />
			{/* Tour Settings Dialog */}
			<TourSettingsDialog
				open={tourSettingsOpen}
				onClose={() => setTourSettingsOpen(false)}
			/>
			{/* New Releases Modal */}
			<NewReleasesModal
				open={newReleasesOpen}
				onClose={() => setNewReleasesOpen(false)}
				currentUser={getUserEmail()}
			/>
			{/* JIRA Ticket Modal */}
			<JiraTicketModal
				open={jiraTicketOpen}
				onClose={() => setJiraTicketOpen(false)}
				currentUser={getUserEmail()}
			/>
			{/* Ticket Status Modal */}
			<TicketStatusModal
				open={ticketStatusOpen}
				onClose={() => setTicketStatusOpen(false)}
				username={getUserEmail()}
			/>
			{/* Project Info Modal */}
			<ProjectInfoModal
				open={projectInfoOpen}
				onClose={() => setProjectInfoOpen(false)}
			/>{" "}
			{/* Cognito User Management Modal */}
			<CognitoUserManagementModal
				open={cognitoModalOpen}
				onClose={() => setCognitoModalOpen(false)}
				currentUser={getUserEmail()}
			/>{" "}
			{/* Notification Banner Admin Modal */}
			<NotificationBannerAdmin
				open={bannerAdminOpen}
				onClose={() => setBannerAdminOpen(false)}
			/>
		</div>
	);
}

export default Header;
